import { normalisePath } from '@/shared/utils/urls'
import type { SortedField } from '@/types'

type HttpMethod = 'GET' | 'PUT' | 'POST' | 'DELETE' | 'PATCH' | 'HEAD'
type AllowedPath = string | string[]
type QueryStringParameter = number|string|SortedField

// Client presets.
export const createAssetManagementClient = () => createClient(new URL(import.meta.env.VITE_ASSET_MANAGEMENT_API_URL))
export const createCoreClient = () => createClient(new URL(import.meta.env.VITE_CORE_URL))
export const createCoreApiClient = () => createClient(new URL(import.meta.env.VITE_API_BASE_URL))

/**
 * Create a new API client for the given target system.
 *
 */
export function createClient(baseUrl: URL) {
  return {
    sendGet: (path: AllowedPath, query: Record<string, QueryStringParameter|QueryStringParameter[]> = { }) =>
      sendRequest('GET', appendQueryToUrl(new URL(normalisePath(path), baseUrl), query)),

    sendPost: (path: AllowedPath, body?: unknown) =>
      sendRequest('POST', new URL(normalisePath(path), baseUrl), body),

    sendPut: (path: AllowedPath, body?: unknown) =>
      sendRequest('PUT', new URL(normalisePath(path), baseUrl), body),

    sendPatch: (path: AllowedPath, body?: unknown) =>
      sendRequest('PATCH', new URL(normalisePath(path), baseUrl), body),

    sendDelete: (path: AllowedPath, query: Record<string, QueryStringParameter|QueryStringParameter[]> = { }) =>
      sendRequest('DELETE', appendQueryToUrl(new URL(normalisePath(path), baseUrl), query), baseUrl),

    buildUrl: (path: AllowedPath) => new URL(normalisePath(path), baseUrl)
  }
}

/**
 * Appends the given query parameters to the given URL, only if the given query parameter is defined.
 *
 */
function appendQueryToUrl(url: URL, query: Record<string, QueryStringParameter|QueryStringParameter[]>) {
  for (const key in query) {
    if (query[key] !== undefined && query[key] !== null) {
      const value = query[key]

      if (Array.isArray(value)) {
        value.forEach(x => url.searchParams.append(key, reduceForQueryString(x)))
      } else {
        url.searchParams.set(key, reduceForQueryString(value))
      }
    }
  }

  return url
}

function reduceForQueryString(value: QueryStringParameter) {
  // Probably a sorted field.
  if (typeof value === 'object' && 'field' in value && 'direction' in value) {
    return `${value.direction === 'asc' ? '' : '-'}${value.field}`
  }

  // Otherwise cast to string.
  return value.toString()
}

/**
 * Performs the actual sending of the request using the browser's fetch() function.
 * All requests are treated as JSON request bodies, and responses are set to only accept JSON.
 *
 */
async function sendRequest(method: HttpMethod, url: URL, body?: unknown, init?: RequestInit) {
  // Ensure we always have an object.
  init = init || { }

  // Always send the necessary credentials.
  init.credentials = "include"

  // Override the method.
  init.method = method.toUpperCase()

  // Always populate headers.
  init.headers = new Headers(init.headers)

  // Ensure we accept JSON as a response.
  init.headers.set('accept', 'application/json')

  if (body !== undefined) {
    init.headers.set('content-type', 'application/json')
    init.body = JSON.stringify(body)
  }

  return fetch(url.toString(), init)
}
